// src/Component/Footer.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Facebook, Linkedin, Twitter, Target, ShieldCheck, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  // --- SCROLL TO TOP LOGIC ---
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // 300px scroll karne ke baad button dikhayega
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- EXACT ROUTE PATHS DEfine YAHAN KAREIN ---
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Market Overview', path: '/market-overview' },
    { name: 'Features', path: '/features' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'FAQ', path: '/faq' },
  ];

  const legalLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'Demat Registration', path: '/demat/register' },
    { name: 'Associate Partner', path: '/partner' },
    { name: 'Disclaimer', path: '/disclaimer' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  // // Dynamic Live Date formatter
  // const todayDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase().replace(/ /g, '.');

  return (
    <>
      <footer className="relative bg-[#05110d] border-t border-gray-900 pt-16 md:pt-20 pb-8 overflow-hidden font-[Urbanist]">
        
        {/* =========================================
            1. TRADING BACKGROUND MAGIC (Glows & Grids)
            ========================================= */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          {/* Subtle Moving Grid */}
          <div 
            className="absolute inset-0 opacity-10" 
            style={{ 
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', 
              backgroundSize: '40px 40px' 
            }} 
          />
          
          {/* Giant Floating Glows (Bullish & Bearish) */}
          <div className="absolute bottom-1/2 left-0 w-[400px] h-[400px] bg-red-900/10 rounded-full blur-[100px] md:blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-900/15 rounded-full blur-[100px] md:blur-[120px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-8 lg:gap-12 mb-16">
            
            {/* =========================================
                LEFT COLUMN: Brand & Reach
                ========================================= */}
            <div className="lg:col-span-4 flex flex-col space-y-6">
              <div>
                {/* BRAND NAME with Neon Green/Red Pulse */}
                <h3 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600 tracking-tight leading-none group">
                  <span className="text-emerald-500 text-4xl">•</span> Motilal Algo
                </h3>
                
                <div className="flex items-center gap-3 mt-3 text-red-500 font-extrabold text-sm border-l-2 border-red-900 pl-3">
                   <ShieldCheck size={18} />
                   <span>SEBI Registered Intermediary</span>
                </div>
              </div>
              
              <p className="text-gray-400 text-sm font-medium leading-relaxed max-w-md">
                Your gateway to smarter, faster trading in the Indian stock market. Stay informed, take action, and elevate your trading journey with robust algorithmic execution.
              </p>

              {/* Glowing Social Media Links */}
              {/* <div className="flex items-center gap-4 pt-2">
                {[Facebook, Linkedin, Twitter].map((Icon, index) => (
                  <a 
                    key={index} 
                    href="#" 
                    className="w-10 h-10 rounded-full bg-slate-900/50 flex items-center justify-center text-emerald-500 border border-emerald-900/40 hover:bg-emerald-600/10 hover:text-red-500 hover:border-red-900/40 transition-all duration-300 shadow-[0_0_15px_rgba(16,185,129,0.1)] group"
                  >
                    <Icon size={18} className="group-hover:animate-pulse" />
                  </a>
                ))}
              </div> */}
            </div>

            {/* =========================================
                MIDDLE COLUMNS: Quick Links (Red Headings)
                ========================================= */}
            <div className="lg:col-span-2 space-y-5">
              <h4 className="text-base font-extrabold uppercase tracking-wider text-red-500">Quick Links</h4>
              <ul className="space-y-3.5 text-sm font-medium text-gray-400">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      onClick={() => window.scrollTo(0, 0)} // Page change pe direct top pe le jayega
                      className="hover:text-emerald-400 transition-colors flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-900 group-hover:bg-red-500 transition-colors" /> {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <h4 className="text-base font-extrabold uppercase tracking-wider text-red-500">Legal</h4>
              <ul className="space-y-3.5 text-sm font-medium text-gray-400">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      onClick={() => window.scrollTo(0, 0)} // Page change pe direct top pe le jayega
                      className="hover:text-emerald-400 transition-colors flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-900 group-hover:bg-red-500 transition-colors" /> {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* =========================================
                RIGHT COLUMN: Contact & CTA
                ========================================= */}
            <div className="lg:col-span-4 lg:pl-6 space-y-6">
              <h4 className="text-base font-extrabold uppercase tracking-wider text-red-500">Contact Us</h4>
              
              <div className="space-y-4 text-sm font-medium text-gray-400">
                  <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-900/30 border border-emerald-900/40 text-emerald-400">
                          <MapPin size={18} />
                      </div>
                      <p className="flex-1 mt-1">Level 11, Canberra Block, UB City, 24 Vittal Mallya Road, Bangalore - 560001.</p>
                  </div>
                  <div className="flex items-center gap-3.5">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-900/30 border border-emerald-900/40 text-emerald-400">
                          <Phone size={18} />
                      </div>
                      <a href="tel:+917562871226" className="flex-1 hover:text-emerald-400 transition-colors">+91 75628 71226</a>
                  </div>
                  <div className="flex items-center gap-3.5">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-900/30 border border-emerald-900/40 text-emerald-400">
                          <Mail size={18} />
                      </div>
                      <a href="mailto:support@motilalalgo.com" className="flex-1 hover:text-emerald-400 transition-colors">support@motilalalgo.com</a>
                  </div>
              </div>

              {/* Action Button (Red) */}
              <div className="pt-4">
                  <Link to="/demat/register" onClick={() => window.scrollTo(0, 0)}>
                      <motion.button 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 text-white px-7 py-3 rounded-xl font-bold text-base shadow-[0_0_20px_rgba(239,68,68,0.3)] transition-all"
                      >
                          <Target size={20} />
                          Open Free Demat Account
                      </motion.button>
                  </Link>
              </div>
            </div>
          </div>

          {/* =========================================
              4. COPYRIGHT & TRADING NOTE 
              ========================================= */}
          <div className="pt-10 border-t border-gray-900/50 mt-10">
            <div className="flex flex-col md:flex-row justify-between gap-6 md:gap-10">
              <div className="space-y-2 max-w-3xl">
                <p className="text-emerald-400 text-sm font-bold tracking-wide">
                  © 2022 Motilal Algo. All rights reserved.
                </p>
                <p className="text-xs text-gray-600 font-medium leading-relaxed">
                  <strong className="text-red-500">• Trading Warning:</strong> F&O Trading (Nifty/BankNifty Options) is highly speculative and subject to high market risk. Please ensure you carefully read the Risk Disclosure Document before trading. Motilal Algo provides automated execution and data widgets only; we are not responsible for any financial losses incurred. Past performance is not indicative of future results.
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-700 font-semibold tracking-wide border border-gray-900 px-3 py-1.5 rounded-md h-fit">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,1)] animate-pulse" />
                {/* Dynamically showing today's date */}
                {/* <span>LIVE: {todayDate} - NOIDA, INDIA</span> */}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* =========================================
          SCROLL TO TOP BUTTON (Floating)
          ========================================= */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 30, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.5 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-colors border border-emerald-400/50 group"
            aria-label="Scroll to top"
          >
            <ArrowUp size={24} className="group-hover:-translate-y-1 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer;